/**
 * O catálogo de telas que o navegador conhece — id, rota e permissão de leitura.
 *
 * **Não é o menu.** O menu inteiro vem do servidor, no manifesto: é ele que decide grupo,
 * rótulo, ícone, ordem e o que cada pessoa alcança, e a tela só desenha o que chegou de lá
 * (`useMenuConfig`). Este arquivo já foi a cópia local disso — grupos, rótulos por afinidade,
 * ícones e pesos —, e a cópia não era lida por ninguém: quem editasse o rótulo aqui veria a
 * tela continuar igual, porque o rótulo que aparece é o do servidor.
 *
 * O que sobrou tem um uso só, e é um uso que o manifesto **não** cobre: o manifesto traz o que
 * a pessoa alcança, e aqui está o que existe. É a diferença entre as duas listas que permite a
 * guarda dizer "esta tela não faz parte do seu acesso" em vez de deixar passar por omissão
 * (`avaliar`, em `authorization/pode.ts`). O estado vazio das listagens usa o mesmo id para
 * achar a rota e a família de permissão do próximo passo (`EmptyState`).
 *
 * Tela nova de menu entra aqui com id, rota e a permissão de ler. Rótulo e posição, não: quem
 * responde por eles é o servidor.
 */

import { TelaDoCatalogo } from '../types/menuConfig';

export const CATALOGO_DE_TELAS: readonly TelaDoCatalogo[] = [
  { id: 'lidera-ia', caminho: '/lidera-ia', permissao: 'lidera_ai.use' },
  { id: 'companies', caminho: '/companies', permissao: 'company.read' },
  { id: 'departments', caminho: '/departments', permissao: 'department.read' },
  { id: 'units', caminho: '/units', permissao: 'unit.read' },
  { id: 'occupations', caminho: '/occupations', permissao: 'occupation.read' },
  { id: 'employees', caminho: '/employees', permissao: 'employee.read' },
  { id: 'providers', caminho: '/providers', permissao: 'provider.read' },
  { id: 'professionals', caminho: '/professionals', permissao: 'professional.read' },
  { id: 'standard-texts', caminho: '/standard-texts', permissao: 'standard_text.read' },
  { id: 'training-types', caminho: '/training-types', permissao: 'training-type.read' },
  { id: 'appointments', caminho: '/appointments', permissao: 'appointment.read' },
  { id: 'schedule-slots', caminho: '/schedule-slots', permissao: 'schedule_slot.read' },
  { id: 'professional-schedules', caminho: '/professional-schedules', permissao: 'professional_schedule.read' },
  { id: 'reception', caminho: '/reception', permissao: 'reception.read' },
  { id: 'medical-consultations', caminho: '/medical-consultations', permissao: 'consultation.read' },
  { id: 'occupational-exams', caminho: '/occupational-exams', permissao: 'occupational_exam.read' },
  { id: 'toxicological-exams', caminho: '/exames-toxicologicos', permissao: 'toxicological_exam.read' },
  { id: 'medical-leaves', caminho: '/medical-leaves', permissao: 'medical_leaves.read' },
  { id: 'absenteeism', caminho: '/absenteismo', permissao: 'medical_leaves.read' },
  { id: 'turnover', caminho: '/rotatividade', permissao: 'employee.read' },
  { id: 'implantacao', caminho: '/implantacao', permissao: 'company.read' },
  { id: 'pricing', caminho: '/precificacao', permissao: 'pricing.view' },
  { id: 'provider-pricing', caminho: '/precificacao/clinicas', permissao: 'pricing.view' },
  { id: 'consultation-history', caminho: '/consultation-history', permissao: 'consultation.read' },
  { id: 'exam-summons', caminho: '/exam-summons', permissao: 'exam_summons.read' },
  { id: 'disabilities', caminho: '/disabilities', permissao: 'disability.read' },
  { id: 'epis', caminho: '/epis', permissao: 'epi.read' },
  { id: 'epcs', caminho: '/epcs', permissao: 'epc.read' },
  { id: 'occupational-risks', caminho: '/occupational-risks', permissao: 'occupational_risk.read' },
  { id: 'source-generators', caminho: '/source-generators', permissao: 'source_generator.read' },
  { id: 'application-groups', caminho: '/application-groups', permissao: 'application_group.read' },
  { id: 'cats', caminho: '/cats', permissao: 'cat.read' },
  { id: 'measurement-equipments', caminho: '/measurement-equipments', permissao: 'measurement_equipment.read' },
  { id: 'environmental-measurements', caminho: '/environmental-measurements', permissao: 'environmental_measurement.read' },
  { id: 'trainings', caminho: '/trainings', permissao: 'training.read' },
  { id: 'cipaa', caminho: '/cipaa', permissao: 'cipaa.read' },
  { id: 'psychosocial', caminho: '/psychosocial', permissao: 'psychosocial.read' },
  { id: 'psychosocial-clube', caminho: '/psychosocial/clube', permissao: 'psychosocial.read' },
  { id: 'certificates', caminho: '/certificates', permissao: 'certificate.read' },
  { id: 'risk-exam-migration', caminho: '/risk-exam-migration', permissao: 'risk_exam_migration.read' },
  { id: 'document-history', caminho: '/documents/history', permissao: 'document_emission.read' },
  { id: 'document-emission', caminho: '/documents/history/emitir', permissao: 'document_emission.read' },
  { id: 'action-plan', caminho: '/action-plan', permissao: 'action_plan.read' },
  { id: 'assinaturas', caminho: '/assinaturas', permissao: 'assinatura.read' },
  { id: 'epi-entregas', caminho: '/epis/entregas', permissao: 'epi_entrega.read' },
  { id: 'vencimentos', caminho: '/vencimentos', permissao: 'vencimento.read' },
  { id: 'users', caminho: '/users', permissao: 'user.read' },
  { id: 'roles', caminho: '/roles', permissao: 'role.read' },
  { id: 'permissions', caminho: '/permissions', permissao: 'permission.read' },
  { id: 'holidays', caminho: '/holidays', permissao: 'holiday.read' },
  { id: 'cid-codes', caminho: '/cid-codes', permissao: 'cid-code.read' },
  { id: 'whatsapp', caminho: '/whatsapp', permissao: 'whatsapp.read' },
  { id: 'email-config', caminho: '/settings/email', permissao: 'email.config.manage' },
  // `help.read` e concedida a todos os papeis: a ajuda descreve as telas e nao mostra dado de
  // ninguem, mas o menu so exibe item que tem permissao declarada.
  { id: 'treinamentos-e-aplicacao', caminho: '/ajuda', permissao: 'help.read' },
  { id: 'proposals', caminho: '/proposals', permissao: 'proposal.read' },
  { id: 'modulos-do-app', caminho: '/modulos-do-app', permissao: 'benefit_module.read' },
  { id: 'consultants', caminho: '/consultants', permissao: 'consultant.read' },
  { id: 'financial-dashboard', caminho: '/financial', permissao: 'invoices.view' },
  { id: 'invoices', caminho: '/financial/invoices', permissao: 'invoices.view' },
  { id: 'platform-fees', caminho: '/financial/platform-fees', permissao: 'platform-fees.view' },
];
